import React, { useState, useEffect, useContext, useRef } from "react";
import "../styles/styles.css";
import "../../../styles/global.css";
import { fetchData } from "../../../utils/methods";
import { useNavigate } from "react-router-dom";
import Loader from "../../Loader/Loader";
import MessageAlert from "../../MessageAlert/MessageAlert";
import { useAlert } from "../../../utils/AlertContext";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import { icons } from "../../../constants";
import { apiRoutes } from "../../../utils/apiRoutes";

// A language's own name for itself ("Srpski", "English"), the usual way
// language pickers label options. Serbian is asked for in Latin script —
// plain "sr" resolves to Cyrillic.
const languageName = (code) => {
  try {
    const locale = code === "sr" ? "sr-Latn" : code;
    const name = new Intl.DisplayNames([locale], { type: "language" }).of(code);
    return name ? name.charAt(0).toLocaleUpperCase(locale) + name.slice(1) : code;
  } catch {
    return code?.toUpperCase() || "";
  }
};

const Languages = ({
  setIsBarOpen,
  setLanguageGeneral,
  openLanguages,
  setOpenLanguages,
  small = false,
  variant = "dropdown",
  // Expanded sidebar: the button shows the full language name, so the
  // choices open as a side popup (like notifications) instead of the
  // collapsed rail's hover strip of short codes.
  expanded = false,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const wrapperRef = useRef(null);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [language, setLanguage] = useState(
    () => localStorage.getItem("language") || "sr"
  );
  const [languages, setLanguages] = useState([]);
  const [sortedLanguages, setSortedLanguages] = useState([]);
  const [errorMessageAlert, setErrorMessageAlert] = useState("");
  const languageRef = useRef(null);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { t } = useContext(TranslationsContext);
  const theme = localStorage.getItem("theme") || "light";

  useEffect(() => {
    const getData = async () => {
      setIsLoadingData(true);
      const result = await fetchData(
        apiRoutes.getLanguages,
        showAlert,
        navigate,
        t
      );
      if (result) setLanguages(result.data);
      setIsLoadingData(false);
    };
    getData();
  }, []);

  useEffect(() => {
    if (languages.length > 0) {
      const sorted = [
        ...languages.filter((lang) => lang === language),
        ...languages.filter((lang) => lang !== language),
      ];
      setSortedLanguages(sorted);
    }
  }, [languages, language]);

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const handleClickLanguage = (lang) => {
    setLanguage(lang);
    setLanguageGeneral(lang);
    setIsBarOpen(false);
    setOpenLanguages(false); // ✅ zatvori
    setIsPopoverOpen(false);
  };

  const usePopover = expanded && !small;

  useEffect(() => {
    if (!usePopover) setIsPopoverOpen(false);
  }, [usePopover]);

  useEffect(() => {
    if (!isPopoverOpen) return undefined;
    const onDocClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsPopoverOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [isPopoverOpen]);

  if (usePopover) {
    return isLoadingData ? (
      <Loader />
    ) : (
      <div
        ref={wrapperRef}
        className="flex-direction-row navbar-languages-wrapper"
      >
        <div
          className="flex-direction-row items-center justify-center pointer selected-language"
          onClick={() => setIsPopoverOpen((v) => !v)}
        >
          <p className="font-12 inria-sans-700 uppercase">{language}</p>
          <span className="navbar-setting-label">{languageName(language)}</span>
        </div>
        {isPopoverOpen && (
          <div className="navbar-language-popover">
            {languages.map((lang) => (
              <button
                key={lang}
                type="button"
                className={`navbar-language-option ${
                  lang === language ? "active" : ""
                }`}
                onClick={() => handleClickLanguage(lang)}
              >
                <span className="navbar-language-option-code">{lang}</span>
                <span className="navbar-language-option-name">
                  {languageName(lang)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <>
        {isLoadingData ? (
          <Loader />
        ) : (
          <div className="flex-direction-row items-center language-inline-list">
            {languages.map((lang) => (
              <p
                key={lang}
                className={`font-12 inria-sans-700 uppercase pointer language-inline-item ${
                  lang === language ? "language-inline-item--active" : ""
                }`}
                onClick={() => handleClickLanguage(lang)}
              >
                {lang}
              </p>
            ))}
          </div>
        )}

        {errorMessageAlert && (
          <MessageAlert
            message={errorMessageAlert}
            type={"error"}
            onClose={() => setErrorMessageAlert("")}
          />
        )}
      </>
    );
  }

  return (
    <>
      {isLoadingData ? (
        <Loader />
      ) : (
        <div
          ref={languageRef}
          className={`flex-direction-row navbar-languages-wrapper ${
            openLanguages ? "hovered" : ""
          }`}
          onMouseEnter={!small ? () => setOpenLanguages(true) : undefined}
          onMouseLeave={!small ? () => setOpenLanguages(false) : undefined}
        >
          <div
            className="flex-direction-row items-center justify-center pointer selected-language"
            onClick={
              small ? () => setOpenLanguages((prev) => !prev) : undefined
            }
          >
            <p className="font-12 inria-sans-700 uppercase">
              {sortedLanguages[0] || ""}
            </p>
            {/* Only shown in the expanded sidebar (see .navbar-setting-label). */}
            {!small && sortedLanguages[0] && (
              <span className="navbar-setting-label">
                {languageName(sortedLanguages[0])}
              </span>
            )}
            {/* {small && (
              <img
                src={
                  theme === "light" ? icons.arrow_down : icons.arrow_down_dark
                }
                style={{
                  transform: !openLanguages ? "rotate(0)" : "rotate(180deg)",
                  transition: "transform 0.1s ease",
                }}
                width={20}
                height={20}
              />
            )} */}
          </div>

          {openLanguages && (
            <div
              className="flex-direction-row items-center other-languages-wrapper"
              ref={languageRef}
            >
              {sortedLanguages.slice(1).map((lang) => (
                <div
                  key={lang}
                  className="flex-direction-row items-center justify-center pointer other-language"
                  onClick={() => handleClickLanguage(lang)}
                >
                  <p className="font-12 inria-sans-700 uppercase">{lang}</p>
                </div>
              ))}
            </div>
          )}

          {errorMessageAlert && (
            <MessageAlert
              message={errorMessageAlert}
              type={"error"}
              onClose={() => setErrorMessageAlert("")}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Languages;
